import React from 'react';
import {
  Wrench, UserPlus, Fuel, Shield, DollarSign, Route, AlertTriangle,
  ChevronDown, ChevronUp, CreditCard, Tag, XCircle,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { VEHICLES } from '../../data/vehicles';
import { Gauge } from '../game/Gauge';
import { VehicleStats } from './VehicleStats';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { computeReturnDate } from '../hr/driverDetail/parts';
import { getVehicleTransferCost, getDepotCityId } from '../../data/depotGeo';
import { getDepotOccupancy } from '../../data/depotEconomics';
import { findCityById } from '../../data/mapCities';
import { localize } from '../../data/i18nData';
import { toast } from '../ui/sonner';

export function VehicleRow({
  v,
  drivers,
  treasury,
  totalDays,
  activeContracts,
  idleDrivers,
  expanded,
  setExpanded,
  getSellPrice,
  handleSell,
  maintainVehicle,
  assignDriverToVehicle,
  computeLeaseBuyout,
  setBuyoutTarget,
  setBreakLeaseTarget,
  fileInsuranceClaim,
  setClaimResult,
  toggleVehicleInterim,
  playSound,
}) {
  const { t } = useTranslation();
  const assignedDriver = drivers.find(d => d.id === v.assignedDriverId);
  const sellPrice = getSellPrice(v);
  const wearCritical = (v.wear || 0) >= 75;
  // 13/06/2026 — Chantier Dépôts Phase 3 : transfert inter-dépôts.
  const ownedDepots = useGameStore(s => s.ownedDepots);
  const allVehicles = useGameStore(s => s.vehicles) || EMPTY_ARRAY;
  const company = useGameStore(s => s.company);
  const transferVehicle = useGameStore(s => s.transferVehicle);
  const forceUnstickVehicle = useGameStore(s => s.forceUnstickVehicle);
  // 26/06/2026 — compteur dynamique pour la maintenance, cohérent avec
  // les compteurs formation/congés du HR.
  const calDay = useGameStore(s => s.day);
  const calMonth = useGameStore(s => s.month);
  const calYear = useGameStore(s => s.year);
  const maintenanceDaysLeft = v.status === 'maintenance' && (v.maintenanceUntil || 0) > 0
    ? Math.max(0, v.maintenanceUntil - (totalDays || 0))
    : 0;
  const maintenanceReturnDate = v.status === 'maintenance' && maintenanceDaysLeft > 0
    ? computeReturnDate(calDay, calMonth, calYear, maintenanceDaysLeft)
    : null;
  // 04/02/2026 — Q1 Bug Messagerie : statut distinct pour les missions flash
  // (opportunités commerciales acceptées via Messagerie). Avant ce fix, le
  // camion passait silencieusement en "active" sans aucune indication, d'où
  // la perception « ça bloque mais rien ne se passe ».
  const commercialOpps = useGameStore(s => s.commercialOpportunities || EMPTY_ARRAY);
  const commercialOpp = v.commercialOpportunityId
    ? commercialOpps.find(o => o.id === v.commercialOpportunityId)
    : null;
  const commercialOppDaysLeft = commercialOpp
    ? Math.max(0, (commercialOpp.endsOnDay || 0) - (totalDays || 0))
    : 0;
  const vehicleDepot = (ownedDepots || []).find(d => d.id === (v.depotId || 'depot-hq'));
  const otherDepots = (ownedDepots || []).filter(d => d.id !== (v.depotId || 'depot-hq'));
  const canTransfer = v.status === 'idle' && !v.assignedContractId && otherDepots.length > 0;
  // 17/06/2026 — Détection incohérence pour P0 bug "véhicule immobilisé".
  // Stuck = `assignedDriverId` set mais (chauffeur introuvable OU indisponible)
  // ET véhicule pas en mission active. Permet d'afficher un CTA "Débloquer".
  const _linkedDriver = v.assignedDriverId
    ? drivers.find(d => d.id === v.assignedDriverId)
    : null;
  const _driverBlocking = _linkedDriver && (
    _linkedDriver.onHoliday === true
    || _linkedDriver.status === 'vacation'
    || _linkedDriver.status === 'sick'
    || _linkedDriver.status === 'striking'
    || _linkedDriver.status === 'training'
  );
  const isStuck = !!v.assignedDriverId && (
    !_linkedDriver
    || (_driverBlocking && v.status !== 'active')
  );
  return (
    <div className={`bg-ink-800 rounded-lg border transition-all ${
      v.status === 'broken' ? 'border-danger/60 bg-danger/5' :
      wearCritical ? 'border-warn/60' : 'border-ink-600'
    }`} data-testid={`vehicle-${v.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-3">
            {(v.image || VEHICLES.find(vd => vd.id === v.dataId)?.image) && (
              <img
                src={v.image || VEHICLES.find(vd => vd.id === v.dataId)?.image}
                alt={`${v.brand} ${v.model}`}
                className="w-24 h-16 rounded-lg object-cover border border-ink-600 flex-shrink-0"
              />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-white font-medium">T{v.tier}</span>
                <h4 className="font-bold text-slate-100">{v.brand} {v.model}</h4>
              </div>
              <p className="text-xs text-slate-500 mt-1">{(localize('vehicle', { id: v.dataId || v.id, specialties: v.specialties }).specialties || []).join(', ')}</p>
            </div>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${
              commercialOpp ? 'bg-purple-500/15 text-purple-300' :
              v.status === 'idle' ? 'bg-ink-700/40 text-slate-400' :
              v.status === 'active' ? 'bg-money/10 text-money' :
              v.status === 'maintenance' ? 'bg-warn/10 text-warn' :
              v.status === 'broken' ? 'bg-danger/15 text-danger' :
              v.status === 'delivery' ? 'bg-blue-500/15 text-blue-300' :
              v.status === 'returning' ? 'bg-amber-500/15 text-amber-300' :
              v.status === 'selling' ? 'bg-purple-500/15 text-purple-300' :
              v.status === 'transfer' ? 'bg-sky-500/15 text-sky-300' :
              'bg-danger/10 text-danger'
            }`}
            data-testid={`vehicle-status-${v.id}`}
            title={
              commercialOpp
                ? `Mission flash pour ${commercialOpp.clientName} — retour J+${commercialOppDaysLeft}, prime ${(commercialOpp.treasuryReward || 0).toLocaleString('fr-FR')} €`
              : v.status === 'maintenance' && maintenanceReturnDate
                ? `Sortie d'atelier prévue le ${maintenanceReturnDate}`
                : undefined
            }
          >
            {commercialOpp ? `Mission flash — J+${commercialOppDaysLeft}`
              : v.status === 'idle' ? t('available_status')
              : v.status === 'active' ? t('active_status')
              : v.status === 'maintenance'
                ? (maintenanceReturnDate
                    ? `Réparation — encore ${maintenanceDaysLeft}j — retour ${maintenanceReturnDate}`
                    : t('maintenance_status'))
              : v.status === 'broken' ? t('broken_status')
              : v.status === 'delivery' ? `Livraison J+${Math.max(0, (v.availableOnDay || 0) - (totalDays || 0))}`
              : v.status === 'returning' ? `Retour au dépôt J+${Math.max(0, (v.returningUntilDay || 0) - (totalDays || 0))}`
              : v.status === 'selling' ? `Vente J+${Math.max(0, (v.saleCompletionDay || 0) - (totalDays || 0))}`
              : v.status === 'transfer' ? t('vehicle_transfer_status')
              : v.status}
          </span>
        </div>

        {v.isLeased && (
          <div className="flex items-center gap-1.5 -mt-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-400 font-medium flex items-center gap-0.5">
              <CreditCard className="w-2.5 h-2.5" />
              Leasing — {formatMoney(v.leaseMonthlyPayment)}/m
            </span>
            <span className="text-[10px] text-slate-500">
              {Math.max(0, Math.ceil(((v.leaseEndDay || 0) - totalDays) / 30))} {t('months_remaining')}
            </span>
          </div>
        )}
        {v.mortgaged && (
          <div className="flex items-center gap-1.5 -mt-1" data-testid={`mortgaged-badge-${v.id}`}>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-money/10 text-money font-medium flex items-center gap-0.5">
              <CreditCard className="w-2.5 h-2.5" />
              Hypothéqué — gage bancaire
            </span>
            <span className="text-[10px] text-slate-500">Revente bloquée tant que le crédit n&apos;est pas soldé</span>
          </div>
        )}
        {v.isUsed && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-warn/15 text-warn font-medium inline-flex items-center gap-0.5 -mt-1">
            <Tag className="w-2.5 h-2.5" /> {t('used_vehicle')} — {t('purchase_at')} {formatMoney(v.purchasePrice)}
          </span>
        )}

        <div className="grid grid-cols-3 gap-3 text-xs mb-2">
          <div
            className="flex items-center gap-1 text-slate-500"
            title={`Carburant : ${formatMoney(v.dailyFuel)}/j en mission · ${formatMoney(Math.round(v.dailyFuel * 0.05))}/j au repos (gardiennage, démarrages)`}
            data-testid={`fuel-cost-${v.id}`}
          >
            <Fuel className="w-3.5 h-3.5" />
            {v.status === 'active' ? (
              <span><span className="text-money font-bold">{formatMoney(v.dailyFuel)}</span>/j</span>
            ) : (
              <span>
                {formatMoney(v.dailyFuel)}<span className="text-slate-600 mx-0.5">→</span>
                <span className="text-warn font-bold">{formatMoney(Math.round(v.dailyFuel * 0.05))}</span>/j
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-slate-500">
            <Wrench className="w-3.5 h-3.5" /> {formatMoney(v.maintenancePerMonth)}/m
          </div>
          <div className="flex items-center gap-1 text-slate-500">
            <Shield className="w-3.5 h-3.5" /> {formatMoney(v.insurancePerMonth)}/m
          </div>
        </div>

        {v.status !== 'active' && !v.isLeased && (
          <p className="text-[10px] text-slate-500 mb-2 flex items-center gap-1" data-testid={`fuel-idle-info-${v.id}`}>
            <Fuel className="w-3 h-3" />
            Au repos (entre deux missions) : <span className="text-warn font-mono">5 %</span> du carburant (gardiennage, démarrages). En mission = 100 %.
          </p>
        )}

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 w-12">{t('wear')}</span>
          <div className="flex-1">
            <Gauge value={v.wear || 0} label="" />
          </div>
          <span className="text-xs font-bold text-slate-400 w-10 text-right">{Math.round(v.wear || 0)}%</span>
        </div>
        {wearCritical && (
          <p className="text-[10px] text-warn flex items-center gap-1 mt-1">
            <AlertTriangle className="w-3 h-3" /> {t('wear_critical')}
          </p>
        )}

        {v.status === 'active' && (() => {
          const ac = (activeContracts || []).find(c => c.vehicleId === v.id);
          if (!ac) return null;
          const total = ac.frequency === 'ponctuel' ? (ac.duration || 1) : (ac.duration || 30);
          const elapsed = Math.max(0, total - (ac.daysRemaining || 0));
          const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
          const linkedDriver = drivers.find(d => d.id === ac.driverId);
          return (
            <div
              className="mt-3 rounded-lg border border-money/40 bg-money/5 p-2.5"
              data-testid={`vehicle-mission-health-${v.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-money font-mono font-bold">
                    <Route className="w-3 h-3" />
                    Mission en cours
                  </div>
                  <p className="text-xs font-semibold text-slate-200 truncate mt-0.5">{ac.name}</p>
                  {linkedDriver && (
                    <p className="text-[10px] text-slate-500">
                      Chauffeur : {linkedDriver.name}
                      {linkedDriver.onHoliday && (
                        <span className="text-amber-300 ml-1">· En congés (retour J{linkedDriver.holidayEndDay})</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-slate-500">{t('days_remaining_short')}</p>
                  <p className="text-base font-black text-money font-mono leading-none" data-testid={`vehicle-mission-days-${v.id}`}>
                    {ac.daysRemaining}<span className="text-[10px] text-slate-500 font-normal">/{total}j</span>
                  </p>
                </div>
              </div>
              <div
                className="mt-1.5 h-1.5 rounded-full bg-ink-700/50 overflow-hidden"
                data-testid={`vehicle-mission-progress-${v.id}`}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  className="h-full bg-money transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px]">
                <span className="text-slate-500">{pct}% accompli</span>
                <span className="text-money font-mono font-bold">
                  {formatMoney(ac.dailyRevenue)}/j
                </span>
              </div>
              {/* 11/06/2026 (soir 9) — Interim driver toggle.
                  10/02/2026 (Lot N P2) — Retour user prod : « toggle
                  clairement visible avec coût d'intérim affiché ».
                  Désormais TOUJOURS visible quand le véhicule a un
                  chauffeur titulaire, pas seulement quand il est en
                  congés. Le coût ne s'applique que pendant l'absence —
                  l'activation préalable agit comme une « assurance ». */}
              {linkedDriver && (() => {
                const interimCostMap = { 1: 120, 2: 180, 3: 250, 4: 320, 5: 400 };
                const dailyCost = interimCostMap[v.tier] || 120;
                const isOnHoliday = linkedDriver.onHoliday;
                return (
                  <div
                    className={`mt-2 px-2 py-1.5 rounded border flex items-center justify-between text-[11px] ${
                      isOnHoliday
                        ? 'border-amber-500/40 bg-amber-500/5'
                        : 'border-ink-600 bg-ink-700/30'
                    }`}
                    data-testid={`interim-toggle-row-${v.id}`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`font-mono uppercase tracking-wider text-[10px] ${
                        isOnHoliday ? 'text-amber-300' : 'text-slate-400'
                      }`}>Intérim</span>
                      <span className="text-slate-400 truncate">
                        {v.useInterim
                          ? isOnHoliday
                            ? `Actif — ${formatMoney(dailyCost)}/j en cours`
                            : `Armé — ${formatMoney(dailyCost)}/j si congés`
                          : isOnHoliday
                            ? `Désactivé — livraisons ratées (${formatMoney(dailyCost)}/j si activé)`
                            : `Désactivé — ${formatMoney(dailyCost)}/j en cas de congés`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleVehicleInterim && toggleVehicleInterim(v.id)}
                      className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold transition-colors ${
                        v.useInterim
                          ? 'bg-amber-500 text-ink-900 hover:bg-amber-400'
                          : 'bg-ink-700 text-amber-300 border border-amber-500/40 hover:bg-amber-500/15'
                      }`}
                      data-testid={`interim-toggle-${v.id}`}
                    >
                      {v.useInterim ? 'ON' : 'OFF'}
                    </button>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        <div className="flex items-center gap-2 mt-3">
          {/* 11/02/2026 (fix user prod) — Retirée l'UI d'assignation
              chauffeur → véhicule depuis la Flotte. L'assignation se fait
              désormais UNIQUEMENT à la prise de contrat (Dashboard →
              accepter un contrat → assistant driver+vehicle). Aucune
              dépendance métier entre un véhicule "propre" et un chauffeur
              en dehors du contrat actif. */}
          {assignedDriver && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <UserPlus className="w-3 h-3" /> {assignedDriver.name}
            </span>
          )}
          {/* 17/06/2026 — P0 bug user : véhicule + chauffeur immobilisés.
              Affiche un CTA explicite quand l'état est détecté incohérent
              (chauffeur fantôme ou en congés/arrêt/formation sur véhicule
              non actif). Action symétrique : détache + libère le contrat. */}
          {isStuck && (
            <button
              onClick={() => {
                if (window.confirm(_linkedDriver
                  ? `Détacher ${_linkedDriver.name} de ce véhicule ?\n\nLe contrat lié sera également retiré. Aucune pénalité — c'est une correction d'incohérence.`
                  : `Effacer le chauffeur fantôme de ce véhicule ?\n\nLe contrat lié sera également retiré.`)) {
                  forceUnstickVehicle(v.id);
                }
              }}
              className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 transition-colors font-mono uppercase tracking-wider"
              data-testid={`force-unstick-${v.id}`}
              title="Détacher le binding chauffeur/contrat (correction d'incohérence)"
            >
              <XCircle className="w-3 h-3" /> Débloquer
            </button>
          )}

          {/* 13/06/2026 — Chantier Dépôts : dépôt d'attache + transfert */}
          {vehicleDepot && (ownedDepots || []).length > 1 && (
            <span
              className="text-[10px] text-slate-500 font-mono flex items-center gap-0.5"
              data-testid={`vehicle-depot-${v.id}`}
              title={`Dépôt d'attache : ${vehicleDepot.name}`}
            >
              📍 {findCityById(getDepotCityId(vehicleDepot, company))?.name || vehicleDepot.name}
            </span>
          )}
          {canTransfer && (
            <select
              onChange={(e) => {
                if (!e.target.value) return;
                const res = transferVehicle(v.id, e.target.value);
                e.target.value = '';
                if (res && !res.ok) {
                  const msg = res.reason === 'target_depot_full'
                    ? `Dépôt cible saturé : ${res.occupancy ?? '?'}/${res.capacity} places occupées par des camions stationnés (les camions en mission ne comptent pas). Libérez une place (transfert/vente) ou agrandissez le parking via l'investissement dépôt « Extension parking » (+5 places).` /* i18n-keep */
                    : res.reason === 'insufficient_funds'
                      ? `Trésorerie insuffisante (convoyage ${(res.cost || 0).toLocaleString('fr-FR')} €).` /* i18n-keep */
                      : 'Transfert impossible.'; /* i18n-keep */
                  alert(msg);
                }
              }}
              className="text-[11px] border border-sky-500/40 text-sky-300 rounded px-1.5 py-1 bg-ink-800 focus:outline-none focus:border-sky-400 transition-colors"
              defaultValue=""
              data-testid={`transfer-vehicle-${v.id}`}
              title={t('vehicle_transfer_tooltip')}
            >
              <option value="" disabled className="bg-ink-800 text-slate-300">
                ⇄ {t('vehicle_transfer_label')}
              </option>
              {otherDepots.map(d => {
                const occ = getDepotOccupancy(d.id, allVehicles, activeContracts);
                const cap = d.capacityVehicles || 10;
                return (
                  <option key={d.id} value={d.id} disabled={occ >= cap} className="bg-ink-800 text-slate-100">
                    {findCityById(getDepotCityId(d, company))?.name || d.name} — {occ}/{cap} places — {formatMoney(getVehicleTransferCost(vehicleDepot, d, company))}
                  </option>
                );
              })}
            </select>
          )}

          <div className="flex gap-1.5 ml-auto">
            <button
              onClick={() => {
                const res = maintainVehicle(v.id);
                if (res && res.ok) {
                  toast.success(`${v.brand} ${v.model} entretenu — ${formatMoney(res.cost)} (usure −40 pts)`);
                } else if (res && res.reason === 'insufficient_funds') {
                  toast.error(`Trésorerie insuffisante : ${formatMoney(res.cost)} requis.`);
                }
              }}
              disabled={(v.wear || 0) < 15 || treasury < v.maintenancePerMonth || v.status === 'selling' || v.status === 'delivery'}
              className="text-xs bg-warn/100 text-white px-3 py-1.5 rounded-md font-medium disabled:opacity-40 hover:bg-amber-600 transition-colors"
              data-testid={`maintain-${v.id}`}
              title={
                v.status === 'selling' ? 'Entretien indisponible — véhicule en cours de vente'
                  : v.status === 'delivery' ? 'Entretien indisponible — véhicule en livraison'
                  : (v.wear || 0) < 15 ? 'Usure < 15 % — entretien inutile pour le moment'
                  : treasury < v.maintenancePerMonth ? `Trésorerie insuffisante (${formatMoney(v.maintenancePerMonth)} requis)`
                  : `Entretien ${formatMoney(v.maintenancePerMonth)} — usure −40 pts`
              }
            >
              <Wrench className="w-3 h-3 inline mr-1" /> {t('repair')}
            </button>
            <button
              onClick={() => handleSell(v)}
              disabled={v.status === 'active' || v.isLeased || v.mortgaged || v.status === 'selling' || v.status === 'delivery'}
              className="text-xs border border-danger/40 text-danger px-3 py-1.5 rounded-md hover:bg-danger/10 disabled:opacity-40 transition-colors"
              data-testid={`sell-${v.id}`}
              title={
                v.status === 'selling' ? 'Vente en cours — bouton désactivé jusqu\'au virement (J+5)'
                  : v.mortgaged ? 'Revente bloquée : véhicule hypothéqué'
                  : v.status === 'delivery' ? 'Revente indisponible — véhicule en livraison'
                  : undefined
              }
            >
              <DollarSign className="w-3 h-3 inline mr-0.5" />
              {v.status === 'selling'
                ? `Vente J+${Math.max(0, (v.saleCompletionDay || 0) - (totalDays || 0))}`
                : `${t('sell_vehicle')} ${formatMoney(sellPrice)}`}
            </button>
            {v.isLeased && (() => {
              const quote = computeLeaseBuyout(v.id);
              const total = quote?.total || 0;
              return (
                <>
                  <button
                    onClick={() => setBuyoutTarget(v)}
                    className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors"
                    data-testid={`buyout-${v.id}`}
                    title={t('buyout_lease_title')}
                  >
                    {t('buyout')} {formatMoney(total)}
                  </button>
                  <button
                    onClick={() => setBreakLeaseTarget(v)}
                    className="text-xs border border-danger/40 text-danger px-3 py-1.5 rounded-md hover:bg-danger/10 transition-colors flex items-center gap-1"
                    data-testid={`break-lease-${v.id}`}
                    title={t('break_lease_title')}
                  >
                    <XCircle className="w-3 h-3" />
                    {t('break_lease')}
                  </button>
                </>
              );
            })()}
          </div>
        </div>

        <button
          onClick={() => setExpanded(expanded ? null : v.id)}
          className="w-full mt-2 text-xs text-slate-500 hover:text-slate-400 flex items-center justify-center gap-1 py-1 transition-colors"
          data-testid={`toggle-stats-${v.id}`}
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Masquer les stats' : 'Voir les stats'}
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <VehicleStats vehicle={v} t={t} />

          <div className="bg-accent/10 rounded-lg p-3 border border-blue-100" data-testid={`insurance-section-${v.id}`}>
            <h5 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> {t('insurance')}
            </h5>
            <div className="text-[10px] text-slate-400 space-y-1 mb-2">
              <p>{t('premium')} : {formatMoney(v.insurancePerMonth)}/{t('month')} — {t('insurance_claims')} : {(v.insuranceClaims || []).length}/3</p>
              {(v.insuranceClaims || []).length < 3 && (
                <p>{t('coverage')} : {Math.round((0.80 - (v.insuranceClaims || []).length * 0.20) * 100)}%</p>
              )}
            </div>

            {(v.insuranceClaims || []).length > 0 && (
              <div className="space-y-1 mb-2">
                {(v.insuranceClaims || []).map(claim => (
                  <div key={claim.id} className="flex items-center justify-between text-[10px] bg-ink-800 rounded px-2 py-1">
                    <span className="text-slate-500">{t('day')} {claim.day} — {t('claim_number')} #{claim.claimNumber}</span>
                    <span className="text-money font-medium">+{formatMoney(claim.reimbursement)}</span>
                    <span className="text-danger">{t('franchise')} {formatMoney(claim.franchise)}</span>
                  </div>
                ))}
              </div>
            )}

            {(v.wear >= 50 || v.status === 'broken') && (v.insuranceClaims || []).length < 3 && (
              <button
                onClick={() => {
                  const repairCost = Math.round(v.maintenancePerMonth * (v.status === 'broken' ? 3 : 1.5));
                  playSound('notification');
                  const result = fileInsuranceClaim(v.id, repairCost);
                  setClaimResult(result);
                  if (result && result.error) {
                    toast.error(result.message || 'Sinistre refusé.');
                  } else if (result) {
                    toast.success(`Dossier sinistre ouvert — ${result.vehicleName}`, {
                      description: `Réparation ${formatMoney(result.repairCost)} réglée · indemnisation ${formatMoney(result.reimbursement)} attendue J${result.payoutDay} (suivi dans « Opérations & sinistres »).`,
                    });
                  }
                  setTimeout(() => setClaimResult(null), 3000);
                }}
                className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover transition-colors w-full"
                data-testid={`file-claim-${v.id}`}
              >
                {t('declare_claim')} ({formatMoney(Math.round(v.maintenancePerMonth * (v.status === 'broken' ? 3 : 1.5)))})
              </button>
            )}

            {(v.insuranceClaims || []).length >= 3 && (
              <p className="text-[10px] text-danger font-medium">{t('max_claims')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
